import { Button, HorizontalDivider, Icon, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Card, AppLoader, Notification } from '@hero/atoms';
import { useBulkUpsertContact } from '@hero/services/contact-service/hooks';

import { ADD_TAG_STEP, UPLOAD_SPREADSHEET_STEP } from './constants';
import * as S from './style';

function ReviewImport({
  setFooterProps,
  setStep,
  tags,
  uploadedRecipients,
  onUpdatingDone,
  onRequestClose
}) {
  const { t } = useTranslation();
  const { mutateAsync: bulkUpsertContact, isLoading: pageLoading } = useBulkUpsertContact();

  useEffect(() => {
    setFooterProps({
      leftButtonText: t('back'),
      leftButtonOnClick: () => {
        setStep(ADD_TAG_STEP);
      },
      isLeftButtonActive: true,
      isRightButtonActive:
        uploadedRecipients.length > 0 && uploadedRecipients.filter(r => r.hasError).length === 0,
      rightButtonOnClick: async () => {
        const contactIds = await bulkUpsertContact({
          contacts: uploadedRecipients,
          tags: tags.map(tag => tag.value)
        });

        if (onUpdatingDone) onUpdatingDone(contactIds);
        onRequestClose();
      },
      rightButtonText: t('import')
    });
  }, [uploadedRecipients, tags]);

  return (
    <>
      {pageLoading && <AppLoader />}
      <S.ReviewHead>
        <S.ReviewHeadTitle>
          <Typography variant="body1Bold">{t('reviewAndImport')}</Typography>
          <Typography variant="body2" color="fg" emphasis="weaker">
            {t('reviewAndImportDescription')}
          </Typography>
        </S.ReviewHeadTitle>
        <Button
          variant="link"
          color="fg"
          onClick={() => {
            setStep(UPLOAD_SPREADSHEET_STEP);
          }}
        >
          {t('startOver')}
        </Button>
      </S.ReviewHead>
      <Card>
        <S.ResultItem>
          <S.ResultTitle variant="body2">{t('contacts')}:</S.ResultTitle>
          <S.ResultBody>
            <S.ResultDescription>
              <Trans
                i18nKey="contactsReadyToBeImported"
                values={{ amount: uploadedRecipients.length }}
                components={{ b: <b /> }}
              />
            </S.ResultDescription>
            <Button
              variant="link"
              color="neutral"
              onClick={() => {
                setStep(UPLOAD_SPREADSHEET_STEP);
              }}
              startIcon={<Icon name="edit" />}
            >
              {t('edit')}
            </Button>
          </S.ResultBody>
        </S.ResultItem>
        <HorizontalDivider $margin="8px 0" />
        <S.ResultItem>
          <S.ResultTitle variant="body2">{t('tags')}:</S.ResultTitle>
          <S.ResultBody>
            {tags.length > 0 ? (
              <S.TagList>
                {tags.map(tag => (
                  <S.TagBadge
                    variant="contained"
                    color="bgDefault"
                    rounded
                    size="sm"
                    key={tag.value}
                    hasAutomation={tag.automations?.length}
                  >
                    {tag.label}
                  </S.TagBadge>
                ))}
              </S.TagList>
            ) : (
              <Notification
                title={t('noAssignedTags') + '.'}
                status="info"
                message={<Typography variant="body2">{t('noAssignedTagsDesc')}</Typography>}
              />
            )}
            <Button
              variant="link"
              color="neutral"
              onClick={() => {
                setStep(ADD_TAG_STEP);
              }}
              startIcon={<Icon name="edit" />}
            >
              {t('edit')}
            </Button>
          </S.ResultBody>
        </S.ResultItem>
      </Card>

      <S.InfoText>{t('dragAndDropFileInfo')}</S.InfoText>
    </>
  );
}

ReviewImport.propTypes = {
  setFooterProps: PropTypes.func,
  setStep: PropTypes.func,
  tags: PropTypes.array,
  uploadedRecipients: PropTypes.func,
  onUpdatingDone: PropTypes.func,
  onRequestClose: PropTypes.func
};

export { ReviewImport };
